import { Injectable } from "@angular/core";
import * as momentTimezone from "moment-timezone";
import {
  OfferPrices,
  OfferRegion,
  offerPrices,
  resolveOfferRegion,
} from "src/app/pages/account/trial-offer.config";
import { MobileService } from "./mobile.service";
import { NativeBridgeService } from "./native-bridge.service";

/**
 * FLIP-537. The one place the app decides whether a user is shown (and charged) US or Australian
 * prices, so the pre-checkout copy and the checkout request always agree.
 *
 * Until a storefront is supplied the region comes from the device's timezone; the start-trial screen
 * supplies the iPhone's App Store storefront, which then wins for the rest of the sign-up flow.
 */
@Injectable({ providedIn: "root" })
export class OfferRegionService {
  private storefrontRegion: OfferRegion | null = null;
  private storefrontRead: Promise<void> | null = null;

  constructor(private mobileService: MobileService, private nativeBridge: NativeBridgeService) {}

  region(): OfferRegion {
    return this.storefrontRegion ?? resolveOfferRegion(null, this.timezoneCountry());
  }

  prices(): OfferPrices {
    return offerPrices(this.region());
  }

  /** Record an iPhone's App Store storefront country (alpha-2); null leaves the timezone in charge. */
  setStorefront(country: string | null): void {
    this.storefrontRegion = country ? resolveOfferRegion(country, null) : null;
  }

  /**
   * Reads the iPhone's App Store storefront once (iOS app only), so every screen that shows prices
   * agrees with the checkout. Anywhere else, or when the read fails, the timezone stays in charge.
   * Never rejects.
   */
  ensureStorefront(): Promise<void> {
    if (!this.storefrontRead) {
      this.storefrontRead =
        this.mobileService.getPlatformType() === "iOS"
          ? this.nativeBridge
              .getStorefront()
              .then((result) => this.setStorefront(result.ok ? result.payload.country : null))
              .catch(() => undefined)
          : Promise.resolve();
    }
    return this.storefrontRead;
  }

  /** "US" when the device's timezone is one of the United States' zones, otherwise null. */
  private timezoneCountry(): string | null {
    try {
      const zone = this.guessTimezone();
      return zone && momentTimezone.tz.zonesForCountry("US").includes(zone) ? "US" : null;
    } catch {
      return null;
    }
  }

  // Separate so tests can supply a timezone.
  protected guessTimezone(): string {
    return momentTimezone.tz.guess();
  }
}
